import { useNavigation } from "@-/com.npmjs.expo-router";

type NavigationLike = {
  getParent: () => unknown;
};

function isNavigationLike(value: unknown): value is NavigationLike {
  return (
    typeof value === "object" &&
    value !== null &&
    "getParent" in value &&
    typeof value.getParent === "function"
  );
}

/**
 * How many navigators sit above the calling screen — 0 for a screen directly under the root
 * navigator, growing by one per nested navigator.
 *
 * The back registry resolves competing publishers by depth (see `createPrimaryControlsStripBackRegistry`),
 * because "which screen is the user looking at" is a structural question, not a timing one.
 * Several `PrimaryControlsStripBack` publishers are legitimately mounted at once — a tab-stack layout and the
 * screen pushed on top of it — and each reports `useIsFocused() === true` within its OWN navigator,
 * so focus alone cannot pick a winner. Depth can: the deepest mounted publisher is the innermost
 * screen, which is the one whose back control should render.
 *
 * The parent chain is fixed for a screen's lifetime (a screen never re-parents), so this reads it
 * once per render without subscribing.
 */
export function useNavigationDepth(): number {
  const navigation = useNavigation();
  let depth = 0;
  let node: unknown = isNavigationLike(navigation) ? navigation.getParent() : undefined;
  // Bounded so a malformed/cyclic parent chain can never spin: real nesting is single digits.
  while (isNavigationLike(node) && depth < 32) {
    depth += 1;
    node = node.getParent();
  }
  return depth;
}
